/**
 * OpenAIGPT4oMiniAdapter — production RubricGrader (Arch §2.7).
 *
 * POSTs the GradeRequest to the same-origin /api/grade endpoint, which
 * forwards it to OpenAI GPT-4o-mini with Structured Outputs. The
 * Worker holds the OPENAI_API_KEY; this adapter never touches it
 * directly (PRD §5).
 *
 * Response mapping: the Worker returns snake_case criterion keys
 * (`position`, `plan_cato`, etc.) because JSON-Schema property names
 * can't contain spaces or parens. This adapter maps them back to the
 * display names the rest of the app uses (`Position`, `Plan (CATO)`).
 *
 * Grading granularity: per hero decision. See `src/types/grade.ts` for
 * the data shape.
 *
 * Local-dev note: in `npm run dev` (Vite, no Worker), `/api/grade`
 * returns 404 and this adapter throws. GradeService maps that to a
 * 'terminal' GradeOutcome and the user lands on the CouldntBeGraded
 * screen. For end-to-end testing run `wrangler dev` instead.
 */

import type {
  CriterionGrade,
  DecisionGrade,
  GradeRequest,
  HeroActionKind,
  RubricCriterion,
  RubricGrade,
} from '@/types/grade';
import { RUBRIC_CRITERIA } from '@/types/grade';
import type { Street } from '@/types/action';
import type { RubricGrader } from './rubricGrader';

const GRADE_ENDPOINT = '/api/grade';

/** Snake_case → display name, mirroring worker/src/types.ts CRITERION_DISPLAY. */
const KEY_TO_DISPLAY: Record<string, RubricCriterion> = {
  position: 'Position',
  default: 'Default',
  spr: 'SPR',
  plan_cato: 'Plan (CATO)',
  board_texture: 'Board Texture',
  target: 'Target',
  range: 'Range',
  inverse_sizing: 'Inverse Sizing',
  hand_class: 'Hand Class',
  bluff_catch_checklist: 'Bluff Catch Checklist',
};

const VALID_STREETS: ReadonlySet<Street> = new Set([
  'flop',
  'turn',
  'river',
]);

const VALID_ACTION_KINDS: ReadonlySet<HeroActionKind> = new Set([
  'check',
  'bet',
  'call',
  'raise',
  'fold',
  'allin',
]);

interface WorkerDecision {
  action_index: number;
  street: 'flop' | 'turn' | 'river';
  action_kind: HeroActionKind;
  criteria: Record<
    string,
    { verdict: 'PASS' | 'PARTIAL' | 'MISSING' | 'NA'; feedback: string }
  >;
  prescribed_default: {
    verdict: 'FOLLOWED' | 'DEVIATED' | 'NA';
    rationale: string | null;
  };
}

interface WorkerSuccess {
  ok: true;
  grade: {
    decisions: WorkerDecision[];
    session_focus_feedback: string | null;
  };
}

interface WorkerError {
  ok: false;
  reason: 'cap_hit' | 'api_error' | 'invalid_response' | 'misconfigured';
  message: string;
}

class OpenAIGPT4oMiniAdapter implements RubricGrader {
  readonly providerId = 'openai';
  readonly modelId = 'gpt-4o-mini';

  async grade(request: GradeRequest): Promise<RubricGrade> {
    const resp = await fetch(GRADE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    // Parse the response body once; works for both success and error
    // shapes since both are JSON.
    let body: WorkerSuccess | WorkerError;
    try {
      body = (await resp.json()) as WorkerSuccess | WorkerError;
    } catch {
      throw new Error(
        `OpenAIAdapter: /api/grade returned non-JSON (status ${resp.status})`,
      );
    }

    if (!resp.ok || !body.ok) {
      const reason = !body.ok ? body.reason : 'api_error';
      const message = !body.ok ? body.message : `HTTP ${resp.status}`;
      throw new Error(`OpenAIAdapter: ${reason} — ${message}`);
    }

    return mapToRubricGrade(body.grade);
  }
}

/**
 * Map the snake_case structured-output shape returned by /api/grade
 * back to the display-named RubricGrade shape used by the rest of the
 * app. Defensive: strict mode in OpenAI Structured Outputs occasionally
 * silently fails, so this function tolerates missing fields and
 * surfaces NA/empty defaults rather than throwing. Exported so the
 * defensive paths can be unit-tested without spinning up `fetch`.
 */
export function mapToRubricGrade(
  raw: WorkerSuccess['grade'],
): RubricGrade {
  // Defensive: even though the JSON schema declares `decisions` required,
  // strict structured-outputs sometimes silently fails (refusals / token
  // limits / temperature variance) and we get back a partial response.
  // Tolerate it — surface what we got rather than throwing and routing
  // the user to CouldntBeGraded.
  const rawDecisions = Array.isArray(raw?.decisions) ? raw.decisions : [];
  if (rawDecisions.length === 0) {
    console.warn(
      '[OpenAIAdapter] response had no decisions array',
      raw,
    );
  }

  const decisions: DecisionGrade[] = [];
  for (const d of rawDecisions) {
    if (!d || typeof d !== 'object') continue;
    const street = d.street as Street | undefined;
    const actionKind = d.action_kind as HeroActionKind | undefined;
    if (!street || !VALID_STREETS.has(street)) {
      console.warn(
        '[OpenAIAdapter] skipping decision with invalid street:',
        d.street,
      );
      continue;
    }
    if (!actionKind || !VALID_ACTION_KINDS.has(actionKind)) {
      console.warn(
        '[OpenAIAdapter] skipping decision with invalid action_kind:',
        d.action_kind,
      );
      continue;
    }
    const actionIndex =
      typeof d.action_index === 'number' && Number.isInteger(d.action_index)
        ? d.action_index
        : decisions.length;

    const criteria = {} as DecisionGrade['criteria'];
    for (const name of RUBRIC_CRITERIA) criteria[name] = NA_CRITERION;
    const rawCriteria =
      d.criteria && typeof d.criteria === 'object' ? d.criteria : {};
    for (const [snake, entry] of Object.entries(rawCriteria)) {
      const display = KEY_TO_DISPLAY[snake];
      if (!display || !entry || typeof entry !== 'object') continue;
      criteria[display] = {
        verdict: entry.verdict,
        feedback: entry.feedback ?? '',
      };
    }
    const pd = d.prescribed_default ?? { verdict: 'NA', rationale: null };
    decisions.push({
      actionIndex,
      street,
      actionKind,
      criteria,
      prescribedDefault: {
        verdict: pd.verdict ?? 'NA',
        rationale: pd.rationale ?? null,
      },
    });
  }

  return {
    decisions,
    sessionFocusFeedback: raw?.session_focus_feedback ?? null,
    // Placeholder — GradeService.stampTimeScores overwrites this with
    // real per-decision + aggregate timing computed from
    // ActionRecord.decisionElapsedMs. The LLM is intentionally not
    // asked for timing.
    timeScore: { onTime: 0, total: 0, avgSeconds: 0 },
  };
}

const NA_CRITERION: CriterionGrade = {
  verdict: 'NA',
  feedback: 'No grade returned for this criterion.',
};

export const openaiAdapter: RubricGrader = new OpenAIGPT4oMiniAdapter();
